import Markup from '~/client/src/shared/models/Markup'

import { IGroundFill } from '../MapBoxViewer/labelGroundTone'
import {
  latOfMercatorY,
  lngOfMercatorX,
  mercatorX,
  mercatorY,
} from '../MapBoxViewer/rasterTileMath'
import { NO_FILL, splitHexAlpha } from './MarkupDrawController'
import {
  TEXT_SCALE_REF_ZOOM,
  textBoxCorners,
  textGroundPerPx,
} from './markupTextScale'
import { textMarkupLayouts } from './textMarkupRegistry'

// The filled markup polygons and text boxes a label can stand on, in paint order: text draws
// above the markup geometry. Lives here rather than in labelGroundTone so that module stays a
// pure colour/geometry calculation with no markup domain behind it.
export function groundFillsFromMarkups(markups: Markup[]): IGroundFill[] {
  const fills: IGroundFill[] = []
  markups.forEach(markup => {
    if (markup.geometryType !== 'Polygon') return
    if (!markup.fillColor || markup.fillColor === NO_FILL) return
    const points = markup.coordinates ?? []
    if (points.length < 3) return
    const { color, opacity } = splitHexAlpha(markup.fillColor)
    const ring = points.map(c => [c.longitude, c.latitude] as [number, number])
    // Ray casting wraps the ring itself, but closing it keeps the input honest.
    ring.push(ring[0])
    fills.push({ ring, colorHex: color, opacity })
  })
  return [...fills, ...textBoxFills(markups)]
}

// A filled text box, as the map draws it: its measured box at the text's ground scale, turned
// about its anchor. Measured only once its faces have settled, as the text layer does; reading
// the face table lets a derivation run again when they settle.
function textBoxFills(markups: Markup[]): IGroundFill[] {
  const filled = markups.filter(
    markup =>
      markup.kind === 'text' &&
      markup.fillColor &&
      markup.fillColor !== NO_FILL,
  )
  const layouts = textMarkupLayouts(filled)
  // Constant across the zooms text scales with the map, which is where labels read it.
  const groundPerPx = textGroundPerPx(TEXT_SCALE_REF_ZOOM)
  return filled.flatMap(markup => {
    const layout = layouts.get(markup.id)
    if (!layout) return []
    const { width, height } = layout
    const { longitude, latitude } = markup.coordinates[0]
    const mx = mercatorX(longitude)
    const my = mercatorY(latitude)
    const corners = textBoxCorners(
      (width / 2) * groundPerPx,
      (height / 2) * groundPerPx,
      markup.rotation ?? 0,
    )
    const ring = [...corners, corners[0]].map(([dx, dy]): [number, number] => [
      lngOfMercatorX(mx + dx),
      latOfMercatorY(my + dy),
    ])
    const { color, opacity } = splitHexAlpha(markup.fillColor)
    return [{ ring, colorHex: color, opacity }]
  })
}
